<template>
  <div class="product-view">
    <UiCard v-if="loading" class="product-view__state">Загрузка товара...</UiCard>

    <UiCard v-else-if="!product" class="product-view__state">
      <div class="product-view__empty-title">Товар не найден</div>
      <div class="product-view__muted">Возможно, товар был удален или перемещен</div>
      <router-link to="/catalog" class="product-view__link">Перейти в каталог</router-link>
    </UiCard>

    <div v-else>
      <div class="product-view__breadcrumbs">
        <router-link to="/">Главная</router-link>
        <span>/</span>
        <router-link to="/catalog">Каталог</router-link>
        <span>/</span>
        <router-link :to="`/catalog?category=${product.categoryId}`">
          {{ product.categoryName }}
        </router-link>
        <span>/</span>
        <span>{{ product.name }}</span>
      </div>

      <div class="product-view__main">
        <div class="product-view__gallery">
          <UiCard>
            <img
              :src="currentImage"
              :alt="product.name"
              class="product-view__image"
              @error="handleImageError"
            />
            <div class="product-view__badges">
              <UiBadge v-if="product.isNew" variant="accent">Новинка</UiBadge>
              <UiBadge v-if="product.isBestseller" variant="accent">Хит продаж</UiBadge>
            </div>
          </UiCard>

          <div v-if="product.images && product.images.length > 1" class="product-view__thumbs">
            <button
              v-for="(image, index) in product.images"
              :key="index"
              type="button"
              :class="['product-view__thumb', { 'is-active': currentImageIndex === index }]"
              @click="currentImageIndex = index"
            >
              <img :src="resolveImage(image)" :alt="product.name" @error="handleImageError" />
            </button>
          </div>
        </div>

        <div class="product-view__info">
          <div class="product-view__title">
            <h1>{{ product.name }}</h1>
            <div class="product-view__muted">Артикул: {{ product.sku }}</div>
            <div v-if="product.rating > 0 || product.reviewCount > 0" class="product-view__rating">
              <UiBadge variant="neutral">Рейтинг {{ product.rating }}</UiBadge>
              <span class="product-view__muted">
                ({{ product.reviewCount }}
                {{ pluralize(product.reviewCount, 'отзыв', 'отзыва', 'отзывов') }})
              </span>
            </div>
            <div class="product-view__muted">
              Производитель:
              <router-link :to="`/catalog?manufacturer=${product.manufacturerId}`">
                {{ product.manufacturerName }}
              </router-link>
            </div>
          </div>

          <div class="product-view__price">
            <div class="product-view__price-main">{{ formatPrice(product.price) }}</div>
            <div
              v-if="product.originalPrice && product.originalPrice > product.price"
              class="product-view__price-old"
            >
              {{ formatPrice(product.originalPrice) }}
            </div>
            <UiBadge v-if="discount > 0" variant="accent">-{{ discount }}%</UiBadge>
            <div class="product-view__muted">Цена за единицу без НДС, НДС рассчитывается при оформлении заказа</div>
          </div>

          <div class="product-view__stock">
            <UiBadge :variant="product.inStock ? 'success' : 'neutral'">
              {{ product.inStock ? 'В наличии' : 'Нет в наличии' }}
            </UiBadge>
            <span v-if="product.unit" class="product-view__unit product-view__muted">
              Единица: {{ product.unit.name }} ({{ product.unit.symbol }})
            </span>
          </div>

          <div class="product-view__cart">
            <template v-if="!isSupplier">
              <div v-if="minimumOrder > 1" class="product-view__muted">
                Минимальный заказ: {{ minimumOrder }} {{ product.unit?.symbol || 'шт.' }}
              </div>
              <div class="product-view__quantity">
                <button
                  type="button"
                  class="product-view__qty-button"
                  @click="decreaseQuantity"
                  :disabled="quantity <= minimumOrder || !product.inStock"
                >
                  -
                </button>
                <UiInput
                  v-model.number="quantity"
                  input-filter="integer"
                  :min="minimumOrder"
                  :readonly="!product.inStock"
                  class="product-view__qty-input"
                  @input="onQuantityInput"
                />
                <button
                  type="button"
                  class="product-view__qty-button"
                  @click="increaseQuantity"
                  :disabled="!product.inStock"
                >
                  +
                </button>
              </div>
              <UiButton
                variant="primary"
                :disabled="!product.inStock"
                @click="product.inStock ? addToCart() : openCallbackModal()"
              >
                {{ product.inStock ? 'Добавить в корзину' : 'Уточнить сроки поставки' }}
              </UiButton>
            </template>
            <div class="product-view__actions">
              <button
                type="button"
                :class="['product-view__link', { 'is-favorite': isFavorite }]"
                @click="toggleFavorite"
              >
                {{ isFavorite ? '♥ В избранном' : '♡ В избранное' }}
              </button>
              <button type="button" class="product-view__link" @click="shareProduct">
                ⤴ Поделиться
              </button>
            </div>
          </div>

          <div v-if="product.shortDescription" class="product-view__description">
            <h3>Описание</h3>
            <p>{{ product.shortDescription }}</p>
          </div>
        </div>
      </div>

      <UiCard class="product-view__tabs">
        <div class="product-view__tab-buttons">
          <button
            v-for="tab in tabOptions"
            :key="tab.value"
            type="button"
            :class="['product-view__tab', { 'is-active': activeTab === tab.value }]"
            @click="activeTab = tab.value"
          >
            {{ tab.label }}
          </button>
        </div>
        <div class="product-view__tab-content">
          <div v-if="activeTab === 'description'">
            <p class="product-view__text">{{ product.description }}</p>
          </div>
          <div v-else-if="activeTab === 'specifications'">
            <UiTable v-if="Object.keys(product.specifications ?? {}).length > 0">
              <tbody>
                <tr v-for="[key, value] in Object.entries(product.specifications ?? {})" :key="key">
                  <td data-label="Характеристика" class="product-view__spec-key">{{ key }}</td>
                  <td data-label="Значение">{{ value }}</td>
                </tr>
              </tbody>
            </UiTable>
            <div v-else class="product-view__muted">Характеристики не указаны</div>
          </div>
          <div v-else-if="activeTab === 'reviews'">
            <div v-if="reviewsLoading" class="product-view__muted">Загрузка отзывов...</div>
            <div v-else-if="reviews.length === 0" class="product-view__state">
              Отзывы пока отсутствуют
            </div>
            <ul v-else class="product-view__reviews">
              <li v-for="review in reviews" :key="review.id" class="product-view__review">
                <div class="product-view__review-head">
                  <span class="product-view__review-author">{{ review.author_name }}</span>
                  <UiBadge variant="neutral">{{ review.rating }} / 5</UiBadge>
                  <span class="product-view__muted">{{ formatReviewDate(review.created_at) }}</span>
                </div>
                <div v-if="review.title" class="product-view__review-title">{{ review.title }}</div>
                <p class="product-view__text">{{ review.comment }}</p>
                <div v-if="review.pros?.length" class="product-view__review-pros">
                  <strong>Достоинства:</strong> {{ review.pros.join(', ') }}
                </div>
                <div v-if="review.cons?.length" class="product-view__review-cons">
                  <strong>Недостатки:</strong> {{ review.cons.join(', ') }}
                </div>
              </li>
            </ul>
          </div>
          <div v-else class="product-view__delivery">
            <div>
              <h4>Способы доставки</h4>
              <ul>
                <li>Курьерская доставка по Москве и МО</li>
                <li>Почта России</li>
                <li>Самовывоз</li>
              </ul>
            </div>
            <div>
              <h4>Способы оплаты</h4>
              <ul>
                <li>Банковской картой</li>
                <li>Банковский перевод</li>
                <li>Наличными при получении</li>
              </ul>
            </div>
          </div>
        </div>
      </UiCard>

      <div class="product-view__recommended">
        <h2>Рекомендуемые товары</h2>
        <div class="product-view__grid">
          <ProductCard
            v-for="recommendedProduct in recommendedProducts"
            :key="recommendedProduct.id"
            :product="recommendedProduct"
          />
        </div>
      </div>
      <CallbackRequestModal
        v-model="showCallbackModal"
        :product-name="product?.name || ''"
        :sku="product?.sku || ''"
        :product-id="product?.id ? Number(product.id) : null"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { getApiBaseUrl } from '@/api/base-url'
import {
  applySeo,
  CallbackRequestModal,
  getGeneratedProductSeoMetadata,
  useAppNotifications,
} from '@/modules/common/public'
import { useCartActions } from '@/modules/order/public'
import { useFavoritesActions } from '@/modules/profile/public'
import { useAuthSession } from '@/modules/auth/public'
import { productApi, transformProduct, type ProductReview } from '@/modules/catalog/api/product.api'
import ProductCard from '@/modules/catalog/components/ProductCard.vue'
import type { Product } from '@/types/catalog'
import UiBadge from '@/shared/ui/UiBadge.vue'
import UiButton from '@/shared/ui/UiButton.vue'
import UiCard from '@/shared/ui/UiCard.vue'
import UiInput from '@/shared/ui/UiInput.vue'
import UiTable from '@/shared/ui/UiTable.vue'

const route = useRoute()
const cartActions = useCartActions()
const favoritesActions = useFavoritesActions()
const authSession = useAuthSession()
const isSupplier = computed(() => authSession.isSupplier.value)
const notificationStore = useAppNotifications()

const loading = ref(false)
const product = ref<Product | null>(null)
const recommendedProducts = ref<Product[]>([])
const currentImageIndex = ref(0)
const quantity = ref(1)
const activeTab = ref('description')
const fallbackImage = '/placeholder-product.svg'
const reviews = ref<ProductReview[]>([])
const reviewsLoading = ref(false)

const showCallbackModal = ref(false)

function openCallbackModal() {
  showCallbackModal.value = true
}

const tabOptions = [
  { value: 'description', label: 'Описание' },
  { value: 'specifications', label: 'Характеристики' },
  { value: 'reviews', label: 'Отзывы' },
  { value: 'delivery', label: 'Доставка' },
]
const apiBaseUrl = getApiBaseUrl()

const isSeededImagePlaceholder = (image: string): boolean => {
  const normalized = image.split('/').pop() ?? image
  return (
    /^(product|microscope|centrifuge|spectrophotometer|chromatograph|pipette|filter|scale)_\d*_(main|stage|rotor|module|cell|tip|panel|detail|\d+)\.jpg$/i.test(
      normalized,
    ) || /^product__[\w]+\.jpg$/i.test(normalized)
  )
}

const currentImage = computed(() => {
  if (!product.value?.images || product.value.images.length === 0) {
    return '/placeholder-product.svg'
  }
  const image =
    product.value.images[currentImageIndex.value] ??
    product.value.images[0] ??
    '/placeholder-product.svg'
  return resolveImage(image)
})

const discount = computed(() => {
  if (!product.value?.originalPrice || !product.value.price) return 0
  return Math.round(
    ((product.value.originalPrice - product.value.price) / product.value.originalPrice) * 100,
  )
})

const isFavorite = computed(() => {
  return product.value ? favoritesActions.isFavorite(product.value.id) : false
})

const minimumOrder = computed(() => Math.max(1, product.value?.minimumOrder ?? 1))

const resolveImage = (image: string) => {
  if (!image) return fallbackImage
  if (isSeededImagePlaceholder(image)) return fallbackImage
  if (image.startsWith('http')) return image
  if (image.startsWith('/')) return `${apiBaseUrl}${image}`
  return `${apiBaseUrl}/uploads/products/images/${image}`
}

const handleImageError = (event: Event) => {
  const target = event.target as HTMLImageElement | null
  if (!target) return
  if (!target.src.endsWith(fallbackImage)) {
    target.src = fallbackImage
  }
}

const formatPrice = (price: number | { amount?: number; currency?: string }): string => {
  let priceValue = 0
  if (typeof price === 'object' && price !== null && !Array.isArray(price)) {
    const amount = (price as { amount?: number }).amount
    if (typeof amount === 'number') {
      priceValue = amount / 100
    } else {
      return 'Цена не указана'
    }
  } else if (typeof price === 'number') {
    priceValue = price
  } else {
    return 'Цена не указана'
  }

  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(priceValue)
}

const formatReviewDate = (dateString: string | null): string => {
  if (!dateString) return ''
  const date = new Date(dateString)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

const loadReviews = async (productId: string) => {
  reviewsLoading.value = true
  try {
    reviews.value = await productApi().getProductReviews(productId, { limit: 20 })
  } catch (error) {
    console.error('Ошибка загрузки отзывов:', error)
    reviews.value = []
  } finally {
    reviewsLoading.value = false
  }
}

const pluralize = (count: number, one: string, few: string, many: string): string => {
  const mod10 = count % 10
  const mod100 = count % 100

  if (mod100 >= 11 && mod100 <= 19) return many
  if (mod10 === 1) return one
  if (mod10 >= 2 && mod10 <= 4) return few
  return many
}

const truncateText = (value: string | undefined, maxLength: number): string => {
  const normalized = (value ?? '').replace(/\s+/g, ' ').trim()
  if (normalized.length <= maxLength) {
    return normalized
  }

  return `${normalized.slice(0, maxLength - 3).trim()}...`
}

const updateProductSeo = (currentProduct: Product) => {
  const descriptionSource =
    currentProduct.shortDescription ||
    currentProduct.description ||
    'Карточка товара в каталоге ЛабАктив.'
  const description = truncateText(descriptionSource, 165)
  const image = currentProduct.images?.[0]
    ? resolveImage(currentProduct.images[0])
    : `${window.location.origin}${fallbackImage}`

  applySeo({
    title: `${currentProduct.name} - купить лабораторное оборудование`,
    description,
    image,
    type: 'product',
    canonical: `/product/${currentProduct.id}`,
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: currentProduct.name,
      description,
      sku: currentProduct.sku,
      image: currentProduct.images?.map((item) => resolveImage(item)) ?? [image],
      category: currentProduct.categoryName,
      brand: currentProduct.manufacturerName
        ? {
            '@type': 'Brand',
            name: currentProduct.manufacturerName,
          }
        : undefined,
      offers: {
        '@type': 'Offer',
        priceCurrency: 'RUB',
        price: Number.isFinite(currentProduct.price) ? currentProduct.price : undefined,
        availability: currentProduct.inStock
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock',
        url: `${window.location.origin}/product/${currentProduct.id}`,
      },
    },
  })
}

const loadProduct = async (productId: string) => {
  loading.value = true
  try {
    const generatedSeo = await getGeneratedProductSeoMetadata(productId).catch(() => null)
    if (generatedSeo) {
      applySeo(generatedSeo)
    }

    const apiProduct = await productApi().getProduct(productId)

    // transformProduct уже обрабатывает цену (Money object -> number)
    const transformed = transformProduct(
      apiProduct as unknown as Record<string, unknown>,
    ) as Product
    product.value = transformed
    quantity.value = Math.max(1, transformed.minimumOrder ?? 1)
    updateProductSeo(transformed)
    loadReviews(productId)

    const popularProducts = await productApi().getPopularProducts(4)
    recommendedProducts.value = popularProducts
      .filter((item) => item.id.toString() !== productId)
      .slice(0, 4)
      .map((item) => transformProduct(item as unknown as Record<string, unknown>) as Product)
  } catch (error) {
    console.error('Ошибка загрузки товара:', error)
    product.value = null
    applySeo({
      title: 'Товар не найден',
      description: 'Запрошенный товар не найден или временно недоступен.',
      canonical: `/product/${productId}`,
      robots: 'noindex,nofollow',
      structuredData: null,
    })
    notificationStore.showError('Ошибка загрузки товара')
  } finally {
    loading.value = false
  }
}

function onQuantityInput(event: Event) {
  const target = event.target as HTMLInputElement
  let value = parseInt(target.value, 10)
  if (Number.isNaN(value) || value < minimumOrder.value) {
    value = minimumOrder.value
  }
  quantity.value = value
}

const increaseQuantity = () => {
  // Product.stock не является остатком выбранного SupplierProduct. Реальный
  // максимум проверяет backend после разрешения конкретного оффера.
  if (product.value?.inStock) {
    quantity.value++
  }
}

const decreaseQuantity = () => {
  if (quantity.value > minimumOrder.value) {
    quantity.value--
  }
}

const addToCart = async () => {
  if (!product.value) return

  let qty = quantity.value
  try {
    try {
      await cartActions.addItem({
        productId: product.value.id,
        name: product.value.name,
        price: product.value.price,
        sku: product.value.sku,
        image: product.value.images?.[0] || '/api/placeholder/100/100',
        quantity: qty,
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : ''
      const minMatch = message.match(/Минимальный заказ[^:]*:\s*(\d+)/i)
      if (!minMatch?.[1]) {
        throw error
      }
      qty = Number(minMatch[1])
      quantity.value = qty
      notificationStore.showWarning(
        `Минимальный заказ - ${qty} шт. Количество установлено автоматически, подтвердите добавление.`,
      )
      return
    }

    notificationStore.showSuccess(
      `${product.value.name} добавлен в корзину (${qty} шт.)`,
      {
        action: { label: 'Перейти в корзину', to: '/cart' },
      },
    )
  } catch (error) {
    notificationStore.showError(
      error instanceof Error ? error.message : 'Ошибка добавления в корзину',
    )
  }
}

const toggleFavorite = () => {
  if (product.value) {
    favoritesActions
      .toggleFavorite(product.value)
      .then((result) => {
        if (result.isFavorite) {
          notificationStore.showSuccess('Товар добавлен в избранное', {
            action: { label: 'Перейти в избранное', to: '/favorites' },
          })
        } else {
          notificationStore.showInfo('Товар удален из избранного')
        }
      })
      .catch(() => {
        notificationStore.showError('Ошибка при работе с избранным')
      })
  }
}

const shareProduct = async () => {
  if (navigator.share && product.value) {
    try {
      await navigator.share({
        title: product.value.name,
        text: product.value.shortDescription || product.value.description,
        url: window.location.href,
      })
    } catch {
      await navigator.clipboard.writeText(window.location.href)
      notificationStore.showSuccess('Ссылка скопирована в буфер обмена')
    }
  } else if (product.value) {
    await navigator.clipboard.writeText(window.location.href)
    notificationStore.showSuccess('Ссылка скопирована в буфер обмена')
  }
}

onMounted(() => {
  const productId = route.params['id'] as string | undefined
  if (productId) {
    loadProduct(productId)
  }
})

watch(
  () => route.params['id'],
  (newId) => {
    if (newId) {
      loadProduct(newId as string)
    }
  },
)
</script>

<style scoped>
.product-view {
  padding: 1.5rem 16px 2.5rem;
  max-width: 1200px;
  margin: 0 auto;
  box-sizing: border-box;
}

.product-view__state {
  text-align: center;
  color: #586578;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
}

.product-view__breadcrumbs {
  display: flex;
  gap: 6px;
  font-size: 12px;
  color: #586578;
  margin-bottom: 16px;
  padding-left: 12px;
  padding-right: 12px;
  box-sizing: border-box;
}

.product-view__breadcrumbs a {
  color: var(--color-accent);
  text-decoration: none;
}

.product-view__breadcrumbs a:hover {
  text-decoration: underline;
}

.product-view__main {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 24px;
}

.product-view__gallery {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.product-view__image {
  width: 100%;
  height: 420px;
  object-fit: cover;
  border-radius: 8px;
}

.product-view__badges {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 8px;
}

.product-view__thumbs {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
  gap: 8px;
}

.product-view__thumb {
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0;
  background: #fff;
  cursor: pointer;
  overflow: hidden;
}

.product-view__thumb.is-active {
  border-width: 2px;
  border-color: var(--color-accent);
}

.product-view__thumb img {
  display: block;
  width: 100%;
  height: 80px;
  object-fit: cover;
}

.product-view__info {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.product-view__title h1 {
  margin: 0 0 6px;
}

.product-view__rating {
  display: flex;
  gap: 8px;
  align-items: center;
}

.product-view__muted {
  font-size: 12px;
  color: #586578;
}

.product-view__muted a {
  color: var(--color-accent);
  text-decoration: none;
}

.product-view__muted a:hover {
  text-decoration: underline;
}

.product-view__price {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: baseline;
}

.product-view__price-main {
  font-size: 24px;
  font-weight: 700;
}

.product-view__price-old {
  text-decoration: line-through;
  color: #586578;
}

.product-view__stock {
  display: flex;
  gap: 8px;
  align-items: center;
}

.product-view__cart {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.product-view__quantity {
  display: flex;
  align-items: stretch;
  max-width: 220px;
}

.product-view__qty-input {
  text-align: center;
  width: 56px;
  height: 40px;
  border-radius: 0;
  border-left: none;
  border-right: none;
  box-sizing: border-box;
}

/* Убираем стрелки input[type=number] */
.product-view__qty-input::-webkit-outer-spin-button,
.product-view__qty-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.product-view__qty-input {
  -moz-appearance: textfield;
}

.product-view__qty-button {
  border: 1px solid #e2e8f0;
  background: #fff;
  width: 40px;
  height: 40px;
  border-radius: 0;
  cursor: pointer;
  box-sizing: border-box;
  flex-shrink: 0;
}

.product-view__qty-button:first-child {
  border-radius: 8px 0 0 8px;
}

.product-view__qty-button:last-child {
  border-radius: 0 8px 8px 0;
}

.product-view__qty-button:hover:not(:disabled) {
  background: #eff6ff;
  border-color: var(--color-accent);
  color: var(--color-accent);
}

.product-view__qty-button:disabled {
  opacity: 0.5;
  cursor: default;
}

.product-view__actions {
  display: flex;
  gap: 12px;
}

.product-view__link {
  border: none;
  background: #eff6ff;
  color: var(--color-accent);
  font-size: 12px;
  cursor: pointer;
  font-weight: 600;
  padding: 8px 14px;
  border-radius: 8px;
  transition: background-color 0.15s;
}

.product-view__link:hover {
  background: #dbeafe;
}

.product-view__link.is-favorite {
  background: #fee2e2;
  color: #dc2626;
}

.product-view__link.is-favorite:hover {
  background: #fecaca;
}

.product-view__description h3 {
  margin: 0 0 6px;
}

.product-view__tabs {
  margin-top: 40px;
}

.product-view__tab-buttons {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.product-view__tab {
  border: 1px solid #e2e8f0;
  background: #fff;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
}

.product-view__tab.is-active {
  background: var(--color-accent);
  color: #fff;
  border-color: var(--color-accent);
}

.product-view__text {
  white-space: pre-line;
}

.product-view__spec-key {
  font-weight: 600;
  width: 40%;
}

/* Убираем собственную рамку UiTable, чтобы не дублировать рамку UiCard с вкладками */
.product-view__tab-content :deep(.ui-table) {
  border: none;
  border-radius: 0;
}

.product-view__delivery {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
}

.product-view__delivery h4 {
  margin: 0 0 8px;
}

.product-view__delivery ul {
  margin: 0;
  padding-left: 20px;
}

.product-view__delivery li {
  margin-bottom: 4px;
}

.product-view__reviews {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.product-view__review {
  padding-bottom: 16px;
  border-bottom: 1px solid #e2e8f0;
}

.product-view__review:last-child {
  padding-bottom: 0;
  border-bottom: none;
}

.product-view__review-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.product-view__review-author {
  font-weight: 600;
}

.product-view__review-title {
  font-weight: 600;
  margin-bottom: 4px;
}

.product-view__review-pros,
.product-view__review-cons {
  font-size: 13px;
  color: #586578;
  margin-top: 4px;
}

.product-view__recommended {
  margin-top: 32px;
}

.product-view__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
}

.product-view__empty-title {
  font-weight: 600;
}

@media (max-width: 960px) {
  .product-view__main {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
